import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import axios from 'axios';

import { handleAxiosError, useModals } from 'core/notifications';

import { Checkbox } from 'uikit/inputs';

import NestedListContainer from '../containers/nestedListContainer';
import NewItemFormButtons from './newItemFormButtons';
import useGlobalQueries from '../hooks/useGlobalQueries';
import getExceedAxiosConfig from '../helpers/getAxiosConfig';

import styles from '../styles/topicFollowingModal.module.scss';

const TopicModalListItem = ({
  id,
  checkboxValue,
  handleSelectionUpdate,
  isCheckboxIndeterminate,
  name,
}) => (
  <Checkbox
    className={classnames(styles.checkbox, { [styles.isSelected]: checkboxValue })}
    id={`${id}-checkbox`}
    label={name}
    value={checkboxValue}
    onChange={handleSelectionUpdate}
    isIndeterminate={isCheckboxIndeterminate}
  />
);

const TopicFollowingModal = ({
  getAxiosConfig = null,
  isSubmitDisabledWithoutTopics = false,
  selectedTopics: value = [],
  shouldMarkAsOnboardedOnClose = false,
  topics = [],
}) => {
  const { currentUser } = useGlobalQueries();
  const modals = useModals();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState(value);
  const [isTopicsNested, setIsTopicsNested] = useState(null);

  const removeModal = (type) => modals.removeModal({ payload: { selectedTopics }, type });

  const handleParentCheckboxChange = ({ flattenedItemIds, isCheckboxIndeterminate }) => {
    const allSelected = flattenedItemIds.every((id) => selectedTopics.includes(id));

    let updatedValues;

    if (isCheckboxIndeterminate || allSelected) {
      updatedValues = selectedTopics.filter((item) => !flattenedItemIds.includes(item));
      setSelectedTopics(updatedValues);
    } else {
      updatedValues = [...selectedTopics, ...flattenedItemIds];
      setSelectedTopics(updatedValues);
    }
  };

  const handleChildCheckboxChange = ({ checkboxValue, id }) => {
    const updatedValues = checkboxValue
      ? [...selectedTopics, id]
      : selectedTopics.filter((currentValue) => currentValue !== id);

    setSelectedTopics(updatedValues);
  };

  const handleCloseModal = async (hasSubmitted = false) => {
    const type = hasSubmitted ? 'submit' : 'close';

    if (!shouldMarkAsOnboardedOnClose) return removeModal(type);

    setIsSubmitting(true);

    const authenticityToken = document.querySelector('[name=csrf-token]')?.content;

    try {
      await axios(getExceedAxiosConfig('mark-as-topic-onboarded', {
        userId: currentUser.data.id,
        authenticityToken,
      }));
      removeModal(type);
    } catch (error) {
      setIsSubmitting(false);
      handleAxiosError(error);
    }
  };

  const handleSubmit = async () => {
    if (!getAxiosConfig) return removeModal('submit');

    setIsSubmitting(true);

    try {
      await axios(getAxiosConfig({ selectedTopics }));
    } catch (error) {
      setIsSubmitting(false);
      return handleAxiosError(error);
    }

    handleCloseModal(true);
  };

  const renderTopics = ({ topics: data, level = 0 }) => {
    if (level !== 0 && isTopicsNested === null) setIsTopicsNested(true);

    return data.map(({ items = [], name, id }) => {
      const hasNestedOptions = Boolean(items.length);
      const flattenedItemIds = items.map((item) => item.id);
      const hasCheckedItems = flattenedItemIds.some((itemId) => selectedTopics.includes(itemId));
      const isCheckboxIndeterminate =
        hasCheckedItems &&
        selectedTopics.filter((selectedId) => flattenedItemIds.includes(selectedId)).length <
          flattenedItemIds.length;

      return (
        <NestedListContainer
          key={id}
          bodyClassName={styles.body}
          hasToggle={hasNestedOptions}
          headerClassName={styles.header}
          header={
            <TopicModalListItem
              name={name}
              id={id}
              handleSelectionUpdate={(checkboxValue) =>
                hasNestedOptions
                  ? handleParentCheckboxChange({ flattenedItemIds, isCheckboxIndeterminate })
                  : handleChildCheckboxChange({ id, checkboxValue })
              }
              isCheckboxIndeterminate={isCheckboxIndeterminate}
              checkboxValue={hasCheckedItems || selectedTopics.includes(id)}
            />
          }
          headerPrepend={<div style={{ width: `${20 * level}px` }} />}
          id={id}
          bodyAriaLabel={name}
          toggleClassName={styles.toggle}
          toggleButtonSize="xsmall"
        >
          {hasNestedOptions && (
            <ul className={styles.bodyContainer}>
              {renderTopics({ topics: items, level: level + 1 })}
            </ul>
          )}
        </NestedListContainer>
      );
    });
  };

  return (
    <>
      <div className={classnames(styles.topics, { [styles.isFlattened]: !isTopicsNested })}>
        {renderTopics({ topics })}
      </div>
      <NewItemFormButtons
        isSubmitDisabled={isSubmitDisabledWithoutTopics && selectedTopics.length === 0}
        isSubmitting={isSubmitting}
        isFullScreen={false}
        onCancel={() => handleCloseModal()}
        onSubmit={handleSubmit}
        submitColor="primary"
      />
    </>
  );
};

TopicFollowingModal.propTypes = {
  getAxiosConfig: PropTypes.func,
  topics: PropTypes.arrayOf(PropTypes.shape({})),
  selectedTopics: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  shouldMarkAsOnboardedOnClose: PropTypes.bool,
  isSubmitDisabledWithoutTopics: PropTypes.bool,
};

export default React.memo(TopicFollowingModal);
